/**
 * DateStep — Course selector + compact calendar
 *
 * Arrow key navigation in calendar grid (roving tabindex),
 * aria-selected on days, aria-label with full date + tee time count.
 */

import { useState, useMemo, useCallback, useRef } from 'preact/hooks'
import { ChevronLeft, ChevronRight, Check } from '../components/icons'
import type { MockCourse } from '../mock-data'

interface DateStepProps {
  courses: MockCourse[]
  selectedCourseId: string | null
  onSelectCourse: (courseId: string) => void
  availableDates: Date[]
  selectedDate: Date | null
  onSelectDate: (date: Date) => void
  timezone?: string
}

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const DAY_LABELS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function getMockTeeTimeCount(date: Date): number {
  const seed = date.getDate() * 7 + date.getMonth() * 31
  return 8 + (seed % 20)
}

function formatDateLabel(date: Date, available: boolean, past: boolean): string {
  const dayName = DAY_LABELS_FULL[date.getDay()]
  const monthName = MONTH_NAMES[date.getMonth()]
  const base = `${dayName} ${date.getDate()} ${monthName} ${date.getFullYear()}`
  if (past) return `${base}, past date`
  if (!available) return `${base}, unavailable`
  return `${base}, ${getMockTeeTimeCount(date)} tee times available`
}

export function DateStep({ courses, selectedCourseId, onSelectCourse, availableDates, selectedDate, onSelectDate, timezone }: DateStepProps) {
  const tz = timezone || 'Australia/Melbourne'

  const today = useMemo(() => new Date(), [])
  const [viewMonth, setViewMonth] = useState(() => {
    if (availableDates.length > 0) {
      return new Date(availableDates[0].getFullYear(), availableDates[0].getMonth(), 1)
    }
    return new Date(today.getFullYear(), today.getMonth(), 1)
  })
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)
  const [focusedIndex, setFocusedIndex] = useState<number>(-1)
  const gridRef = useRef<HTMLDivElement>(null)

  const availableDateSet = useMemo(() => {
    const set = new Set<string>()
    availableDates.forEach(d => set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`))
    return set
  }, [availableDates])

  const isDateAvailable = (date: Date) => availableDateSet.has(`${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`)
  const isToday = (date: Date) => isSameDay(date, today)
  const isSelected = (date: Date) => selectedDate ? isSameDay(date, selectedDate) : false
  const isPast = (date: Date) => {
    const d = new Date(date); d.setHours(0, 0, 0, 0)
    const t = new Date(today); t.setHours(0, 0, 0, 0)
    return d < t
  }

  const calendarDays = useMemo(() => {
    const year = viewMonth.getFullYear()
    const month = viewMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days: (Date | null)[] = []
    for (let i = 0; i < firstDay; i++) days.push(null)
    for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d))
    return days
  }, [viewMonth])

  const monthLabel = new Intl.DateTimeFormat('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: tz,
  }).format(viewMonth)

  const handleCalendarKeyDown = useCallback((e: KeyboardEvent, index: number) => {
    const dayCount = calendarDays.length
    let nextIndex = index

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault()
        nextIndex = index + 1
        while (nextIndex < dayCount && !calendarDays[nextIndex]) nextIndex++
        break
      case 'ArrowLeft':
        e.preventDefault()
        nextIndex = index - 1
        while (nextIndex >= 0 && !calendarDays[nextIndex]) nextIndex--
        break
      case 'ArrowDown':
        e.preventDefault()
        nextIndex = index + 7
        break
      case 'ArrowUp':
        e.preventDefault()
        nextIndex = index - 7
        break
      case 'Home':
        e.preventDefault()
        nextIndex = calendarDays.findIndex(d => d !== null)
        break
      case 'End':
        e.preventDefault()
        for (let i = dayCount - 1; i >= 0; i--) {
          if (calendarDays[i]) { nextIndex = i; break }
        }
        break
      default:
        return
    }

    if (nextIndex >= 0 && nextIndex < dayCount && calendarDays[nextIndex]) {
      setFocusedIndex(nextIndex)
      const buttons = gridRef.current?.querySelectorAll<HTMLButtonElement>('button[data-day-index]')
      buttons?.forEach(btn => {
        if (btn.dataset.dayIndex === String(nextIndex)) btn.focus()
      })
    }
  }, [calendarDays])

  // Only show "All" if there are multiple selectable (non-comingSoon) courses
  const selectableCourses = courses.filter(c => !c.comingSoon)
  const showAll = selectableCourses.length > 1

  return (
    <div>
      {/* Course Selector */}
      <div className="bw2-section-label" id="bw2-course-label">Course</div>
      <div className="bw2-course-selector" role="radiogroup" aria-labelledby="bw2-course-label">
        {showAll && (
          <button
            className={`bw2-course-chip ${selectedCourseId === 'all' ? 'bw2-course-chip--selected' : ''}`}
            onClick={() => onSelectCourse('all')}
            type="button"
            role="radio"
            aria-checked={selectedCourseId === 'all'}
          >
            {selectedCourseId === 'all' && <Check size={9} strokeWidth={3} />}
            All
          </button>
        )}
        {courses.map(course => {
          const isComingSoon = course.comingSoon === true
          const isSelected = selectedCourseId === course.id
          return (
            <button
              key={course.id}
              className={`bw2-course-chip ${isSelected ? 'bw2-course-chip--selected' : ''} ${isComingSoon ? 'bw2-course-chip--disabled' : ''}`}
              onClick={() => !isComingSoon && onSelectCourse(course.id)}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-disabled={isComingSoon}
              disabled={isComingSoon}
              title={isComingSoon ? 'Coming soon' : course.description}
            >
              {isSelected && <Check size={9} strokeWidth={3} />}
              {course.name}
              {isComingSoon && <span style={{ fontSize: '7px', opacity: 0.6, marginLeft: '2px' }}>Coming Soon</span>}
            </button>
          )
        })}
      </div>

      {/* Calendar */}
      <div className="bw2-section-label" style={{ marginTop: '6px' }}>When to play</div>

      <div className="bw2-recessed" style={{ marginBottom: '4px' }}>
        <div className="bw2-calendar" role="group" aria-label={`Calendar: ${monthLabel}`}>
          <div className="bw2-calendar__header">
            <span className="bw2-calendar__month" aria-live="polite">{monthLabel}</span>
            <div className="bw2-calendar__nav">
              <button className="bw2-calendar__nav-btn" onClick={() => setViewMonth(p => new Date(p.getFullYear(), p.getMonth() - 1, 1))} type="button" aria-label="Previous month">
                <ChevronLeft size={12} />
              </button>
              <button className="bw2-calendar__nav-btn" onClick={() => setViewMonth(p => new Date(p.getFullYear(), p.getMonth() + 1, 1))} type="button" aria-label="Next month">
                <ChevronRight size={12} />
              </button>
            </div>
          </div>

          <div className="bw2-calendar__grid" ref={gridRef} role="grid" aria-label="Choose a date">
            <div role="row" style={{ display: 'contents' }}>
              {DAY_LABELS.map((label, i) => (
                <div key={label} className="bw2-calendar__day-label" role="columnheader" aria-label={DAY_LABELS_FULL[i]}>{label}</div>
              ))}
            </div>
            {calendarDays.map((date, index) => {
              if (!date) return <div key={`empty-${index}`} className="bw2-calendar__day bw2-calendar__day--empty" role="gridcell" />
              const available = isDateAvailable(date)
              const selected = isSelected(date)
              const past = isPast(date)
              const disabled = !available || past
              const isFocused = focusedIndex === index
              const classes = [
                'bw2-calendar__day',
                available && !past && 'bw2-calendar__day--available',
                selected && 'bw2-calendar__day--selected',
                disabled && 'bw2-calendar__day--disabled',
                isToday(date) && 'bw2-calendar__day--today',
              ].filter(Boolean).join(' ')

              return (
                <button
                  key={date.toISOString()}
                  className={classes}
                  onClick={() => !disabled && onSelectDate(date)}
                  onMouseEnter={() => available && !past && setHoveredDate(date)}
                  onMouseLeave={() => setHoveredDate(null)}
                  onFocus={() => setFocusedIndex(index)}
                  onKeyDown={(e) => handleCalendarKeyDown(e as unknown as KeyboardEvent, index)}
                  disabled={disabled}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  aria-label={formatDateLabel(date, available, past)}
                  tabIndex={selected || (focusedIndex === -1 && isToday(date)) || isFocused ? 0 : -1}
                  data-day-index={index}
                  title={available && !past ? `${getMockTeeTimeCount(date)} tee times` : undefined}
                >
                  {date.getDate()}
                  {available && !past && hoveredDate && isSameDay(date, hoveredDate) && !selected && (
                    <span className="bw2-calendar__tooltip" aria-hidden="true">
                      {getMockTeeTimeCount(date)} times
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '7px', color: 'var(--bw2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--bw2-font-label)' }}>
          <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--bw2-accent)', boxShadow: '0 0 3px var(--bw2-accent-glow)' }} aria-hidden="true" />
          Available
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '7px', color: 'var(--bw2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--bw2-font-label)' }}>
          <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'var(--bw2-border)' }} aria-hidden="true" />
          Unavailable
        </div>
      </div>
    </div>
  )
}
